import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SEO } from '../../seo/useSEO';
import { t } from '../../i18n/pt';
import { api, extractError } from '../../lib/api';
import { Button } from '../../components/ui/Button';
import { useAuthStore } from '../../stores/authStore';
import styles from './Auth.module.scss';

type Status = 'loading' | 'success' | 'error';

export default function VerifyEmailPage(): JSX.Element {
  const [sp] = useSearchParams();
  const token = sp.get('token') ?? '';
  const fetchMe = useAuthStore((s) => s.fetchMe);
  const [status, setStatus] = useState<Status>('loading');
  const [message, setMessage] = useState<string>(t.auth.verifyLoading);
  // Guard against the effect running twice (React 18 StrictMode) — the token is
  // single-use, so a second POST would always report "expired".
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;

    if (!token) {
      setStatus('error');
      setMessage(t.auth.verifyMissingToken);
      return;
    }

    void (async () => {
      try {
        await api.post('/auth/verify-email', { token });
        setStatus('success');
        setMessage(t.auth.verifySuccess);
        // Refresh the session so the banner/checkout gate clears.
        await fetchMe();
      } catch (err) {
        setStatus('error');
        setMessage(extractError(err, t.auth.verifyError));
      }
    })();
  }, [token, fetchMe]);

  return (
    <>
      <SEO title={t.auth.verifyTitle} noindex />
      <div className={styles.wrapper}>
        <div className={styles.card}>
          <header className={styles.intro}>
            <h1 className={styles.title}>{t.auth.verifyTitle}</h1>
            <p className={status === 'error' ? styles.error : styles.lead}>{message}</p>
          </header>
          {status === 'success' ? (
            <Button to="/conta" variant="primary" size="lg" fullWidth>
              {t.auth.verifyCtaAccount}
            </Button>
          ) : null}
        </div>
      </div>
    </>
  );
}
